import { FormEvent, useState } from 'react';
import { createPractitionerProspect } from '@/lib/api';

const inputClass =
  'w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition';
const labelClass = 'block text-xs font-semibold text-foreground-700 mb-1.5';

// Formulaire « Créer ma fiche gratuite » : crée une fiche prospect avec
// les informations nécessaires pour publier sa fiche après validation.
export default function ProspectForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus('sending');
    setFormError('');

    try {
      const formData = new FormData(form);
      const payload = Object.fromEntries(
        Array.from(formData.entries()).map(([key, value]) => [key, String(value)]),
      );
      await createPractitionerProspect(payload);
      setStatus('success');
      form.reset();
    } catch (error) {
      setStatus('error');
      setFormError(error instanceof Error ? error.message : "Une erreur est survenue lors de l'envoi. Merci de réessayer.");
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10">
        <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
          <i className="ri-check-line text-2xl w-6 h-6 flex items-center justify-center" />
        </span>
        <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">Demande enregistrée !</h3>
        <p className="text-sm text-foreground-700 max-w-md leading-relaxed">
          Votre fiche prospect est créée avec le statut standard. Notre équipe la vérifie et vous
          contacte sous 24 h ouvrées pour la mise en ligne.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 px-5 py-2.5 rounded-md bg-background-100 hover:bg-background-200 border border-background-300 text-foreground-800 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          Créer une autre fiche
        </button>
      </div>
    );
  }

  return (
    <form data-readdy-form id="practitioner-prospect-form" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="prospect-name" className={labelClass}>Nom complet</label>
          <input id="prospect-name" name="name" type="text" required placeholder="Ex. Dr. Karim Alaoui" className={inputClass} />
        </div>
        <div>
          <label htmlFor="prospect-email" className={labelClass}>E-mail</label>
          <input id="prospect-email" name="email" type="email" required placeholder="vous@exemple.com" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="prospect-phone" className={labelClass}>Téléphone</label>
          <input id="prospect-phone" name="phone" type="tel" placeholder="+212 6 00 00 00 00" className={inputClass} />
        </div>
        <div>
          <label htmlFor="prospect-establishment" className={labelClass}>Établissement</label>
          <input id="prospect-establishment" name="etablissement" type="text" placeholder="Ex. Cabinet Atlas" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="prospect-specialty" className={labelClass}>Spécialité / profession</label>
          <input id="prospect-specialty" name="specialite" type="text" required maxLength={255} placeholder="Ex. Cardiologue" className={inputClass} />
        </div>
        <div>
          <label htmlFor="prospect-category" className={labelClass}>Catégorie</label>
          <input id="prospect-category" name="category" type="text" required maxLength={255} placeholder="Ex. Médecins" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="prospect-city" className={labelClass}>Ville</label>
          <input id="prospect-city" name="ville" type="text" required maxLength={255} placeholder="Ex. Rabat" className={inputClass} />
        </div>
        <div>
          <label htmlFor="prospect-district" className={labelClass}>Quartier</label>
          <input id="prospect-district" name="quartier" type="text" required maxLength={255} placeholder="Ex. Agdal" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <div>
          <label htmlFor="prospect-address" className={labelClass}>Adresse</label>
          <input id="prospect-address" name="adresse" type="text" maxLength={255} placeholder="Ex. 12 Av. Hassan II" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="prospect-message" className={labelClass}>Message</label>
        <textarea
          id="prospect-message"
          name="message"
          rows={4}
          maxLength={500}
          placeholder="Présentez votre activité en quelques lignes..."
          className={`${inputClass} resize-none`}
        />
        <p className="mt-1 text-xs text-foreground-400">500 caractères maximum.</p>
      </div>

      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="mc-hp-field" />

      {status === 'error' && formError && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 border border-accent-300 px-4 py-3">
          <i className="ri-error-warning-line text-accent-700 text-base w-4 h-4 flex items-center justify-center mt-0.5" />
          <p className="text-xs text-accent-900 leading-relaxed">{formError}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
      >
        {status === 'sending' ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base w-4 h-4 flex items-center justify-center" />
            Envoi en cours...
          </>
        ) : (
          <>
            <i className="ri-add-circle-line text-base w-4 h-4 flex items-center justify-center" />
            Créer ma fiche gratuite
          </>
        )}
      </button>
    </form>
  );
}